// ============================================================
// A/B Testing — deterministic, client-side, zero-latency.
//
// HOW IT WORKS:
//   visitorId + experimentId → stable hash → variant assignment.
//   Same visitor always sees the same variant. No DB, no API call.
//   Assignments are attached to every analytics event automatically.
//
// HOW TO ADD AN EXPERIMENT:
//   1. Add an entry to EXPERIMENTS below
//   2. Use the useExperiment() hook in your component
//   3. Query results: db.analyticsevents.aggregate([
//        { $match: { eventName: 'purchase_confirmed' } },
//        { $group: { _id: '$experiments.your_experiment_id', count: { $sum: 1 } } }
//      ])
//   4. Set active: false when done (old events keep their assignments)
// ============================================================

export interface Experiment {
  id: string;
  variants: string[];
  /** Optional weights (must sum to 1). Defaults to equal split. */
  weights?: number[];
  active: boolean;
}

// ---- DEFINE EXPERIMENTS HERE ----
// Example:
//   'pricing_cta_color': {
//     id: 'pricing_cta_color',
//     variants: ['control', 'orange_cta'],
//     active: true,
//   },
//   'onboarding_flow_v2': {
//     id: 'onboarding_flow_v2',
//     variants: ['control', 'short_flow', 'guided_flow'],
//     weights: [0.5, 0.25, 0.25],
//     active: true,
//   },

export const EXPERIMENTS: Record<string, Experiment> = {
  signup_first_funnel: {
    id: "signup_first_funnel",
    variants: ["control", "signup_first"],
    weights: [0.5, 0.5],
    active: false,
  },
  signup_modal_layout_v1: {
    id: "signup_modal_layout_v1",
    variants: ["control", "pfpmaker_layout"],
    weights: [0, 1],
    active: false,
  },
  /**
   * Editor v2 (ai-avatars / ai-headshots): guests who tap Generate see a fake “Loading” %
   * up to 90%, then the auth modal — instead of the modal opening immediately.
   * Flip: set `active: true` and e.g. `weights: [0, 1]` for all-on, or `[0.5, 0.5]` for A/B.
   */
  guest_editor_v2_fake_loader_before_auth: {
    id: "guest_editor_v2_fake_loader_before_auth",
    variants: ["off", "on"],
    weights: [1, 0],
    active: false,
  },
  /**
   * Dashboard pricing (`/dashboard/pricing`): control = current Pricing V2, v2_1 = alternate copy/layout.
   * Set `active: true` and optional `weights` (e.g. [0.5, 0.5]) to run the test.
   */
  pricing_page_v2_1: {
    id: "pricing_page_v2_1",
    variants: ["control", "v2_1"],
    weights: [0.5, 0.5],
    active: false, // v2_1 won — now the permanent default (see pricing page)
  },
  /**
   * Mobile pricing card order on `/dashboard/pricing`.
   * CONCLUDED 2026-09-18: control won. Default is Starter → Pro → Premium
   * (config order) on all viewports. Pro-first did not lift checkout or
   * shift mix to Pro; T1-ex-US checkout went to zero. Kept inactive for
   * historical attribution. `pro_first` is a non-prod override only
   * (`?exp_pricing_mobile_order_v1=pro_first`).
   *
   * Guest credit packs stay removed for non-subscribers; credit top-ups stay
   * subscriber-only below the plan grid (both arms, not part of the crown).
   */
  pricing_mobile_order_v1: {
    id: "pricing_mobile_order_v1",
    variants: ["control", "pro_first"],
    weights: [1, 0],
    active: false, // control won — Starter → Pro → Premium is the default
  },
  /**
   * Promo banner v1: does showing a promotional banner to visitors increase pricing page
   * views, checkout starts, and purchases?
   *
   * control → no banner
   * banner  → promo banner shown
   */
  promo_banner_v1: {
    id: "promo_banner_v1",
    variants: ["control", "banner"],
    weights: [0.5, 0.5],
    active: false,
  },
  /**
   * Landing destination: does sending new visitors straight to the AI Avatars editor
   * produce more signups, checkout starts, and purchases than landing on the dashboard?
   *
   * control   → /dashboard          (shows all tools)
   * ai_avatars → /editor-v2/ai-avatars  (deep-links straight into the hero tool)
   *
   * Funnel tracked: Visitors → Signed Up → Pricing Viewed → Checkout Started → Purchased
   */
  landing_destination_v1: {
    id: "landing_destination_v1",
    variants: ["control", "ai_avatars"],
    weights: [0.5, 0.5],
    active: false,
  },
  /**
   * Landing homepage CTA (assigned on pfpmaker.com). PAUSED — both arms now
   * get the winning button CTA. Dashboard tray split moved to
   * `dashboard_free_tray_v1`. Kept inactive for historical attribution.
   * Same id/variants as pfpmaker-landing so old events still hash.
   */
  landing_hero_cta_v1: {
    id: "landing_hero_cta_v1",
    variants: ["control", "upload_cta"],
    weights: [0.5, 0.5],
    active: false,
  },
  /**
   * Homepage hero CTA trust subline (assigned on pfpmaker.com).
   * CONCLUDED: control won. Default is no trust line.
   * control    → button CTA as-is
   * trust_line → same button + "Try free. No credit card needed."
   * Primary: CTR on `/`. Same id/variants as pfpmaker-landing so events hash.
   * Assignment is on the landing site; this catalog entry is for analytics.
   */
  landing_hero_cta_trust_v1: {
    id: "landing_hero_cta_trust_v1",
    variants: ["control", "trust_line"],
    weights: [0.5, 0.5],
    active: false,
  },
  /**
   * Homepage hero social proof (assigned on pfpmaker.com).
   * CONCLUDED 2026-09-07: control won. Default is static “18M+ / 55M+” + logos.
   * control → static “18M+ / 55M+” + logos strip
   * ticker  → live created-count, logos strip hidden
   * Primary: CTR on `/` into the app. Same id/variants as pfpmaker-landing.
   * Assignment is on the landing site; this catalog entry is for analytics.
   */
  landing_hero_proof_v1: {
    id: "landing_hero_proof_v1",
    variants: ["control", "ticker"],
    weights: [0.5, 0.5],
    active: false, // control won — static proof + logos is the landing default
  },
  /**
   * Free PFP tray on dashboard arrival.
   * CONCLUDED 2026-09-07: tray won. Live default is the free editor tray
   * (auth-gated download) so the SERP/landing “free” promise is fulfilled.
   * Same-day webapp return was 13.3% tray vs 9% control. Funnel work after
   * arrival is the next lever — this flag stays inactive so analytics no
   * longer split traffic. Control is a non-prod override only
   * (`?exp_dashboard_free_tray_v1=control`).
   */
  dashboard_free_tray_v1: {
    id: "dashboard_free_tray_v1",
    variants: ["control", "tray"],
    weights: [0, 1],
    active: false, // tray won — now the permanent dashboard default
  },
  /**
   * Cross-sell v2: which downstream product niche wins on pfpmaker traffic?
   * companion | looksmax | ai-photo-editor — 33/33/33 sticky assignment.
   * CONCLUDED: looksmax won tier-1 (esp. mobile). Superseded by looksmax_copy_v1.
   */
  cross_sell_v2: {
    id: "cross_sell_v2",
    variants: ["companion", "looksmax", "ai-photo-editor"],
    weights: [1 / 3, 1 / 3, 1 / 3],
    active: false,
  },
  /**
   * Looksmax copy test (Phase 2): offer is fixed to looksmax; we test which
   * sitewide banner copy angle drives the most clicks. Sticky by visitor
   * (hash+weights). Variant attribution attaches automatically to every event;
   * report by experiments.looksmax_copy_v1. Offer field stays "looksmax".
   *
   * Sitewide only: provocation / identity at 50/50.
   * Pruned (weight 0, kept for historical attribution): control, curiosity, outcome.
   * Avatar-grid placement is locked to provocation (not part of this A/B).
   *
   * PAUSED: sitewide falls back to deal banner; styles-list cards off
   * (ENABLE_*_CROSS_SELL_PROMO in cross-sell-v2.ts).
   */
  looksmax_copy_v1: {
    id: "looksmax_copy_v1",
    variants: ["control", "curiosity", "provocation", "identity", "outcome"],
    weights: [0, 0, 0.5, 0.5, 0],
    active: false,
  },
  /**
   * Face AI unlock funnel — screen / copy variants (legacy placeholder).
   */
  face_ai_funnel_screens_v1: {
    id: "face_ai_funnel_screens_v1",
    variants: ["control", "variant_b"],
    weights: [1, 0],
    active: false,
  },
  /**
   * Face AI funnel structure (concluded).
   * - control: Intent → Gaps → Assemble → Pay → Report (5-step, no Preview)
   * - preview_shell: Intent → Gaps → Assemble → Preview → Pay → Report
   * Winner: preview_shell — now the permanent default.
   * Kept inactive for historical attribution on old events.
   */
  face_ai_funnel_shell_v1: {
    id: "face_ai_funnel_shell_v1",
    variants: ["control", "preview_shell"],
    weights: [0, 1],
    active: false, // preview_shell won — permanent Assemble→Preview→Pay
  },
  /**
   * Face AI offer package (legacy pitch variants).
   * Product sell now lives on Preview (gated shell); Pay is checkout-only.
   * Kept for analytics continuity / future CTA copy tests.
   */
  face_ai_offer_package_v1: {
    id: "face_ai_offer_package_v1",
    variants: ["control", "potential_protocol"],
    weights: [0, 1],
    active: true,
  },
  /**
   * Quiz + report color theme — dark | light.
   * CONCLUDED: dark won for both genders — now the permanent default
   * (see FaceAiFunnel.tsx / reportTheme.ts). Kept inactive for historical
   * attribution. Still on events as properties.preview_theme when product tags it.
   * Local FunnelDevNav can override to light for QA.
   */
  face_ai_preview_theme_v1: {
    id: "face_ai_preview_theme_v1",
    variants: ["dark", "light"],
    weights: [1, 0],
    active: false,
  },
  /**
   * Sealed funnel — Stage 1 (PLAN-FACE-AI-OFFER-CONVERSION-ROADMAP §0-D / §2.3).
   * control → report-style preview + Ship-A offer
   * sealed  → sealed showcase preview + transformation-led offer
   * CONCLUDED: sealed won — now the permanent default (see FaceAiFunnel.tsx /
   * StepSealedPreview.tsx). Kept inactive for historical attribution.
   * Still on events as properties.sealed_funnel when product tags it.
   */
  face_ai_sealed_funnel_v1: {
    id: "face_ai_sealed_funnel_v1",
    variants: ["control", "sealed"],
    weights: [0, 1],
    active: false,
  },
  /**
   * Preview → Pay CTA sub-line (see PLAN-FACE-AI-PREVIEW-CONVERSION.md §4).
   * control    → "Web report + PDF · pay once · no subscription" (small, gray)
   * argued_ask → "Not happy with your results? 7-day refund guarantee" (accent color, bigger, prominent)
   * CONCLUDED: argued_ask won — now the permanent default (see
   * FunnelReportPreview.tsx). Kept inactive for historical attribution.
   */
  face_ai_preview_ask_v1: {
    id: "face_ai_preview_ask_v1",
    variants: ["control", "argued_ask"],
    weights: [0, 1],
    active: false,
  },
  /**
   * Engage (quiz) funnel — inter-basin challenger vs sealed champion
   * (PLAN-FACE-AI-OFFER-CONVERSION-ROADMAP §0-D).
   * sealed → intent → gaps → assemble → sealed preview → offer → pay
   * engage → compressed Maxxing-style quiz → same offer → pay
   * CONCLUDED: engage (quiz) won — now the permanent default (see
   * FaceAiFunnel.tsx). Kept inactive for historical attribution.
   * Still on events as properties.engage_funnel when product tags it.
   * Local FunnelDevNav can override to sealed + gender for QA / quiz ABs.
   */
  face_ai_engage_funnel_v1: {
    id: "face_ai_engage_funnel_v1",
    variants: ["sealed", "engage"],
    weights: [0, 1],
    active: false,
  },
  /**
   * Reveal offer — quiz's offer screen as the REVEAL of what build "built".
   * control → StepOffer (sealed copy: PDF thumbs, 3 price mentions)
   * reveal  → StepOfferReveal
   * CONCLUDED: reveal (Offer B) won — now the permanent default
   * (see FaceAiFunnel.tsx). Kept inactive for historical attribution.
   * Still on events as properties.offer_reveal when product tags it.
   * Local FunnelDevNav can override to control (Offer A) for QA.
   */
  face_ai_offer_reveal_v1: {
    id: "face_ai_offer_reveal_v1",
    variants: ["control", "reveal"],
    weights: [0, 1],
    active: false,
  },
  /**
   * Offer hero / plan-list on crowned StepOfferReveal
   * (PLAN-FACE-AI-OFFER-CONVERSION-ROADMAP: face_ai_offer_hero_v1).
   * control → generic models + locked plan
   * yours   → user NOW photo + generated AFTER (US 18+ only)
   * open    → generic models + all plan steps readable
   * CONCLUDED: open (C) won — now the permanent default
   * (see FaceAiFunnel.tsx). Kept inactive for historical attribution.
   * Still on events as properties.offer_hero when product tags it.
   * Local FunnelDevNav can override to control / yours for QA.
   */
  face_ai_offer_hero_v1: {
    id: "face_ai_offer_hero_v1",
    variants: ["control", "yours", "open"],
    weights: [0, 0, 1],
    active: false,
  },
  /**
   * Psych pivot (PLAN-FACE-AI-OFFER-CONVERSION-ROADMAP §0-E direction 1).
   * face  → crowned funnel, face plan / C reveal (control A)
   * psych → Self-Image Profile re-skin + 10-question block (arm B)
   * CONCLUDED 2026-08-18: split crown — female → psych, male → face C.
   * Male psyche follow-up (face_ai_male_psych_quiz_v1) concluded 2026-08-20:
   * C reveal won. Live male test is properties.male_quiz_v2.
   */
  face_ai_psych_pivot_v1: {
    id: "face_ai_psych_pivot_v1",
    variants: ["face", "psych"],
    weights: [0.5, 0.5],
    active: false,
  },
  /**
   * CONCLUDED 2026-08-26: JTBD variant crowned winner.
   * (+0.8pp paywall rate, better qualitative traffic filtering).
   * Live default is JTBD in `app/dashboard/page.tsx` — this flag stays
   * inactive so analytics no longer split traffic. Control is a non-prod
   * override only (`?exp_dashboard_jtbd_v1=control`).
   */
  dashboard_jtbd_v1: {
    id: "dashboard_jtbd_v1",
    variants: ["control", "jtbd"],
    weights: [0, 1],
    active: false,
  },
  /**
   * Male-only: crowned C reveal (control) vs same C reveal + 10 m2 psych
   * questions. CONCLUDED 2026-08-20: C reveal won. Psyche-coded +10 killed
   * male conversion (content, not length). Successor is properties.male_quiz_v2
   * (gn=m only, never experiments.*). Historical attribution only.
   */
  face_ai_male_psych_quiz_v1: {
    id: "face_ai_male_psych_quiz_v1",
    variants: ["control", "quiz"],
    weights: [1, 0],
    active: false,
  },
  /**
   * Face AI report PRICE test (offer frame held constant; only price + anchor
   * moved). First true price A/B on Face AI — every prior face_ai_* test held
   * price fixed at $9.
   *
   * CONCLUDED 2026-09-07: control ($9) won. $14.99 killed male conversion
   * and did not win purchase rate; live default is $9 (was $25). Historical
   * attribution only. In-flight $14.99 PaymentIntents still fulfill via
   * `resolveFaceAiStripePrice`.
   *
   * control      → $9   (was $25) — live default
   * price_14_99  → $14.99 (was $50)
   */
  face_ai_price_v1: {
    id: "face_ai_price_v1",
    variants: ["control", "price_14_99"],
    weights: [1, 0],
    active: false,
  },
};

/** @see EXPERIMENTS.guest_editor_v2_fake_loader_before_auth */
export const GUEST_EDITOR_V2_FAKE_LOADER_EXPERIMENT_ID = "guest_editor_v2_fake_loader_before_auth" as const;

/** @see EXPERIMENTS.pricing_page_v2_1 */
export const PRICING_PAGE_V2_1_EXPERIMENT_ID = "pricing_page_v2_1" as const;

/** @see EXPERIMENTS.pricing_mobile_order_v1 */
export const PRICING_MOBILE_ORDER_V1_EXPERIMENT_ID =
  "pricing_mobile_order_v1" as const;

export type PricingMobileOrderV1Variant = "control" | "pro_first";

/** Winner default: control. `pro_first` is historical / non-prod override only. */
export function resolvePricingMobileOrderVariant(
  variant: string | null | undefined
): PricingMobileOrderV1Variant {
  return variant === "pro_first" ? "pro_first" : "control";
}

/** @see EXPERIMENTS.promo_banner_v1 */
export const PROMO_BANNER_V1_EXPERIMENT_ID = "promo_banner_v1" as const;

/** @see EXPERIMENTS.landing_destination_v1 */
export const LANDING_DESTINATION_V1_EXPERIMENT_ID = "landing_destination_v1" as const;

/** @see EXPERIMENTS.dashboard_jtbd_v1 */
export const DASHBOARD_JTBD_V1_EXPERIMENT_ID = "dashboard_jtbd_v1" as const;

export type DashboardJtbdV1Variant = "control" | "jtbd";

/** @see EXPERIMENTS.landing_hero_cta_v1 */
export const LANDING_HERO_CTA_V1_EXPERIMENT_ID = "landing_hero_cta_v1" as const;

export type LandingHeroCtaV1Variant = "control" | "upload_cta";

/** @see EXPERIMENTS.landing_hero_cta_trust_v1 */
export const LANDING_HERO_CTA_TRUST_V1_EXPERIMENT_ID =
  "landing_hero_cta_trust_v1" as const;

export type LandingHeroCtaTrustV1Variant = "control" | "trust_line";

export function resolveLandingHeroCtaTrustVariant(
  variant: string | null | undefined
): LandingHeroCtaTrustV1Variant {
  return variant === "trust_line" ? "trust_line" : "control";
}

/** @see EXPERIMENTS.landing_hero_proof_v1 */
export const LANDING_HERO_PROOF_V1_EXPERIMENT_ID =
  "landing_hero_proof_v1" as const;

export type LandingHeroProofV1Variant = "control" | "ticker";

/** Winner default: control. `ticker` is historical attribution only. */
export function resolveLandingHeroProofVariant(
  variant: string | null | undefined
): LandingHeroProofV1Variant {
  return variant === "ticker" ? "ticker" : "control";
}

export function resolveLandingHeroCtaVariant(
  variant: string | null | undefined
): LandingHeroCtaV1Variant {
  return variant === "upload_cta" ? "upload_cta" : "control";
}

/** @see EXPERIMENTS.dashboard_free_tray_v1 */
export const DASHBOARD_FREE_TRAY_V1_EXPERIMENT_ID = "dashboard_free_tray_v1" as const;

export type DashboardFreeTrayV1Variant = "control" | "tray";

/** Winner default: tray. `control` is non-prod override only. */
export function resolveDashboardFreeTrayVariant(
  variant: string | null | undefined
): DashboardFreeTrayV1Variant {
  return variant === "control" ? "control" : "tray";
}

/** Winner default: jtbd. `control` is non-prod override only. */
export function resolveDashboardJtbdVariant(
  variant: string | null | undefined
): DashboardJtbdV1Variant {
  return variant === "control" ? "control" : "jtbd";
}

/** @see EXPERIMENTS.cross_sell_v2 */
export const CROSS_SELL_V2_EXPERIMENT_ID = "cross_sell_v2" as const;

/** @see EXPERIMENTS.looksmax_copy_v1 */
export const LOOKSMAX_COPY_V1_EXPERIMENT_ID = "looksmax_copy_v1" as const;

/** @see EXPERIMENTS.face_ai_funnel_screens_v1 */
export const FACE_AI_FUNNEL_SCREENS_V1_EXPERIMENT_ID =
  "face_ai_funnel_screens_v1" as const;

/** @see EXPERIMENTS.face_ai_funnel_shell_v1 */
export const FACE_AI_FUNNEL_SHELL_V1_EXPERIMENT_ID =
  "face_ai_funnel_shell_v1" as const;

export type FaceAiFunnelShellV1Variant = "control" | "preview_shell";

/** @see EXPERIMENTS.face_ai_offer_package_v1 */
export const FACE_AI_OFFER_PACKAGE_V1_EXPERIMENT_ID =
  "face_ai_offer_package_v1" as const;

export type FaceAiOfferPackageV1Variant =
  | "control"
  | "potential_protocol";

/** @see EXPERIMENTS.face_ai_preview_theme_v1 */
export const FACE_AI_PREVIEW_THEME_V1_EXPERIMENT_ID =
  "face_ai_preview_theme_v1" as const;

export type FaceAiPreviewThemeV1Variant = "dark" | "light";

/** @see EXPERIMENTS.face_ai_preview_ask_v1 */
export const FACE_AI_PREVIEW_ASK_V1_EXPERIMENT_ID =
  "face_ai_preview_ask_v1" as const;

export type FaceAiPreviewAskV1Variant = "control" | "argued_ask";

/** @see EXPERIMENTS.face_ai_sealed_funnel_v1 */
export const FACE_AI_SEALED_FUNNEL_V1_EXPERIMENT_ID =
  "face_ai_sealed_funnel_v1" as const;

export type FaceAiSealedFunnelV1Variant = "control" | "sealed";

/** Winner default: sealed (experiment concluded). */
export function resolveSealedFunnelVariant(
  variant: string | null | undefined
): FaceAiSealedFunnelV1Variant {
  return variant === "control" ? "control" : "sealed";
}

/** @see EXPERIMENTS.face_ai_engage_funnel_v1 */
export const FACE_AI_ENGAGE_FUNNEL_V1_EXPERIMENT_ID =
  "face_ai_engage_funnel_v1" as const;

export type FaceAiEngageFunnelV1Variant = "sealed" | "engage";

/** Winner default: engage/quiz (experiment concluded). */
export function resolveEngageFunnelVariant(
  variant: string | null | undefined
): FaceAiEngageFunnelV1Variant {
  return variant === "sealed" ? "sealed" : "engage";
}

/** @see EXPERIMENTS.face_ai_offer_reveal_v1 */
export const FACE_AI_OFFER_REVEAL_V1_EXPERIMENT_ID =
  "face_ai_offer_reveal_v1" as const;

export type FaceAiOfferRevealV1Variant = "control" | "reveal";

/** Winner default: reveal / Offer B (experiment concluded). */
export function resolveOfferRevealVariant(
  variant: string | null | undefined
): FaceAiOfferRevealV1Variant {
  return variant === "control" ? "control" : "reveal";
}

/** @see EXPERIMENTS.face_ai_offer_hero_v1 */
export const FACE_AI_OFFER_HERO_V1_EXPERIMENT_ID =
  "face_ai_offer_hero_v1" as const;

export type FaceAiOfferHeroV1Variant = "control" | "yours" | "open";

/** Winner default: open / Offer C (experiment concluded). */
export function resolveOfferHeroVariant(
  variant: string | null | undefined
): FaceAiOfferHeroV1Variant {
  if (variant === "control" || variant === "yours") return variant;
  return "open";
}

/** @see EXPERIMENTS.face_ai_psych_pivot_v1 */
export const FACE_AI_PSYCH_PIVOT_V1_EXPERIMENT_ID =
  "face_ai_psych_pivot_v1" as const;

export type FaceAiPsychPivotV1Variant = "face" | "psych";

/** Historical helper. Live defaults are gender-locked in FaceAiFunnel. */
export function resolvePsychPivotVariant(
  variant: string | null | undefined
): FaceAiPsychPivotV1Variant {
  return variant === "psych" ? "psych" : "face";
}

/** @see EXPERIMENTS.face_ai_male_psych_quiz_v1 */
export const FACE_AI_MALE_PSYCH_QUIZ_V1_EXPERIMENT_ID =
  "face_ai_male_psych_quiz_v1" as const;

export type FaceAiMalePsychQuizV1Variant = "control" | "quiz";

/** Historical helper. Live male test is properties.male_quiz_v2. */
export function resolveMalePsychQuizVariant(
  variant: string | null | undefined
): FaceAiMalePsychQuizV1Variant {
  return variant === "quiz" ? "quiz" : "control";
}

/** @see EXPERIMENTS.face_ai_price_v1 */
export const FACE_AI_PRICE_V1_EXPERIMENT_ID = "face_ai_price_v1" as const;

export type FaceAiPriceV1Variant = "control" | "price_14_99";

/** Historical helper. Live default is $9; `price_14_99` is fulfillment-only. */
export function resolveFaceAiPriceV1Variant(
  variant: string | null | undefined
): FaceAiPriceV1Variant {
  return variant === "price_14_99" ? "price_14_99" : "control";
}

/**
 * Male looksmaxxing quiz v2 (2026-08-20).
 * control  → C reveal, Set A spine only (DevNav only)
 * maxxing  → C reveal + instrumental quiz (DevNav only)
 * store    → maxxing quiz + sealed-store offer (CROWNED 2026-08-21 — live default)
 *
 * NOT in EXPERIMENTS. Exposure = gn=m + properties.male_quiz_v2.
 * Women still hash via getActiveExperiments; they must never receive this key.
 * Historical splits: ⅓/⅓/⅓ → `:split2` 50/50 maxxing vs store → store 100%.
 */
export type FaceAiMaleQuizV2Variant = "control" | "maxxing" | "store";

export const MALE_QUIZ_V2_PROPERTY = "male_quiz_v2" as const;

/** Winner default: store. Call only when gn=m. control/maxxing = DevNav path override. */
export function assignMaleQuizV2(
  _visitorId?: string
): FaceAiMaleQuizV2Variant {
  return "store";
}

/**
 * Product path after the psych-pivot split crown.
 * face        → C reveal, no extra questions (DevNav / unknown gn)
 * psych       → Self-Image Profile + 10 questions (female champion)
 * male_v2     → looksmaxxing quiz + C reveal offer (DevNav)
 * male_store  → looksmaxxing quiz + sealed-store offer (male live default)
 */
export type FaceAiFunnelPath = "face" | "psych" | "male_v2" | "male_store";

export function resolveFunnelPath(args: {
  gender: "m" | "f" | null;
  maleQuizV2: FaceAiMaleQuizV2Variant | null;
}): FaceAiFunnelPath {
  if (args.gender === "f") return "psych";
  if (args.gender === "m") {
    if (args.maleQuizV2 === "control") return "face";
    if (args.maleQuizV2 === "maxxing") return "male_v2";
    return "male_store";
  }
  return "face";
}

export function isMaleMaxxingQuizPath(path: FaceAiFunnelPath): boolean {
  return path === "male_v2" || path === "male_store";
}

// ---- ASSIGNMENT ENGINE ----

/**
 * Dev/QA override (non-production builds only).
 * A variant can be forced via `?exp_<experimentId>=<variant>` query param;
 * the dashboard page persists it to localStorage so it survives navigation.
 * LocalStorage key format: `exp_override_<experimentId>`.
 * Has no effect in production builds.
 */
function getVariantOverride(experiment: Experiment): string | null {
  if (typeof window === "undefined") return null;
  if (process.env.NODE_ENV === "production") return null;
  try {
    const override = window.localStorage.getItem(`exp_override_${experiment.id}`);
    if (override && experiment.variants.includes(override)) return override;
  } catch {
    // localStorage unavailable — ignore
  }
  return null;
}

/**
 * Read a non-prod `exp_override_<id>` even when the experiment is inactive.
 * Used by concluded tests whose winner is now the product default.
 */
export function getDevExperimentOverride(experimentId: string): string | null {
  const experiment = EXPERIMENTS[experimentId];
  if (!experiment) return null;
  return getVariantOverride(experiment);
}

function stableHash(str: string): number {
  // FNV-1a 32-bit — fast, good distribution, deterministic
  let hash = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return hash >>> 0; // ensure unsigned
}

function assignVariant(experiment: Experiment, visitorId: string): string {
  const hash = stableHash(`${visitorId}:${experiment.id}`);
  const normalized = hash / 0xffffffff; // 0..1

  if (experiment.weights && experiment.weights.length === experiment.variants.length) {
    let cumulative = 0;
    for (let i = 0; i < experiment.weights.length; i++) {
      cumulative += experiment.weights[i];
      if (normalized < cumulative) return experiment.variants[i];
    }
    return experiment.variants[experiment.variants.length - 1];
  }

  // Equal split
  const index = hash % experiment.variants.length;
  return experiment.variants[index];
}

/**
 * Get the variant for a single experiment.
 * Returns null if experiment doesn't exist or is inactive.
 */
export function getVariant(experimentId: string, visitorId: string): string | null {
  const experiment = EXPERIMENTS[experimentId];
  if (!experiment?.active) return null;
  return getVariantOverride(experiment) ?? assignVariant(experiment, visitorId);
}

/**
 * Get all active experiment assignments for a visitor.
 * Attached to every analytics event automatically.
 */
export function getActiveExperiments(visitorId: string): Record<string, string> {
  const assignments: Record<string, string> = {};
  for (const [id, exp] of Object.entries(EXPERIMENTS)) {
    if (exp.active) {
      assignments[id] = getVariantOverride(exp) ?? assignVariant(exp, visitorId);
    }
  }
  return assignments;
}
